import {jsPDF} from "jspdf";
import moment from "moment";
import {
	compareMatchEvents,
	matchEventTime,
	matchEventBelongsToSide,
} from "@/utils/matchEvents";

const STAT_LABELS = {
	minutes: "Minuti",
	goals: "Goal fatti",
	assists: "Assist",
	yellowCards: "Gialli",
	redCard: "Espulsioni",
	goalsConceded: "Goal subiti",
	rating: "Voto",
	strengths: "Punti di forza",
	improvements: "Aree di miglioramento",
	duelsWon: "Duelli vinti",
	duelsTotal: "Duelli totali",
	saves: "Parate",
	outOfAreaReadings: "Letture fuori area",
	lowReadings: "Letture area basse",
	highReadings: "Letture area alte",
	shortHorizontalPassLeft: "Passaggi corti orizzontali sx",
	shortHorizontalPassRight: "Passaggi corti orizzontali dx",
	shortVerticalPass: "Passaggi corti verticali",
	killerPass: "Killer pass",
	longPassLeft: "Passaggi lunghi sx",
	longPassRight: "Passaggi lunghi dx",
	longFrontalPass: "Passaggi lunghi frontali",
	tacklesWon: "Contrasti vinti",
	tacklesAttempted: "Contrasti tentati",
	interceptions: "Intercetti",
	aerialDuelsWon: "Duelli aerei vinti",
	aerialDuelsTotal: "Duelli aerei totali",
	recoveries: "Recuperi",
	foulsCommitted: "Falli commessi",
	foulsSuffered: "Falli subiti",
	defensiveErrors: "Errori difensivi",
	totalPasses: "Passaggi totali",
	successfulPasses: "Passaggi riusciti",
	progressivePasses: "Passaggi progressivi",
	keyPasses: "Passaggi chiave",
	throughPasses: "Passaggi filtranti",
	totalTouches: "Tocchi totali",
	touchesInOppositionBox: "Tocchi in area avversaria",
	pressures: "Pressioni",
	successfulPressures: "Pressioni riuscite",
	defensiveCoverages: "Coperture difensive",
	possessionsUnderPressure: "Possessi sotto pressione",
	totalShots: "Tiri totali",
	shotsOnTarget: "Tiri in porta",
	expectedGoals: "xG",
	expectedAssists: "xA",
	crossesAttempted: "Cross tentati",
	crossesCompleted: "Cross riusciti",
	dribblesAttempted: "Dribbling tentati",
	dribblesSuccessful: "Dribbling riusciti",
	ballsLost: "Palloni persi",
};
const SOURCES = {
	events: "Da eventi",
	manual: "Manuale",
	conflict: "Da confrontare",
	uncollected: "Non raccolto",
};
const EVENT_LABELS = {
	goal: "Goal",
	own_goal: "Autogol (punto avversario)",
	penalty_scored: "Rigore segnato",
	penalty_missed: "Rigore sbagliato",
	injury_exit: "Uscita per infortunio",
	substitution: "Sostituzione",
	yellow_card: "Giallo",
	double_yellow_card: "Doppio giallo",
	red_card: "Rosso",
};
const safe = (value, fallback = "-") =>
	value == null || value === "" ? fallback : String(value);
const personName = (p) => [p?.firstName, p?.lastName].filter(Boolean).join(" ");
const printable = (value) =>
	safe(value).replace(/[–—]/g, "-").replace(/’/g, "'").replace(/′/g, "'");

export const generateMatchPDF = (matchData, teamPlayers = []) => {
	const doc = new jsPDF({unit: "pt", format: "a4"});
	const width = doc.internal.pageSize.getWidth(),
		height = doc.internal.pageSize.getHeight();
	const pad = 40,
		usable = width - pad * 2,
		bottom = height - 48;
	const navy = [11, 31, 68],
		blue = [16, 89, 191],
		ink = [30, 41, 59],
		muted = [100, 116, 139],
		border = [220, 227, 236];
	let y = pad;
	const text = (
		value,
		x,
		at,
		size = 9,
		bold = false,
		color = ink,
		options = {},
	) => {
		doc.setFont("helvetica", bold ? "bold" : "normal");
		doc.setFontSize(size);
		doc.setTextColor(...color);
		doc.text(
			Array.isArray(value) ? value.map(printable) : printable(value),
			x,
			at,
			options,
		);
	};
	const wrap = (value, max, size = 9) => {
		doc.setFont("helvetica", "normal");
		doc.setFontSize(size);
		return doc.splitTextToSize(printable(value), max);
	};
	const need = (space) => {
		if (y + space > bottom) {
			doc.addPage();
			y = pad;
			return true;
		}
		return false;
	};
	const paragraph = (value, color = muted) => {
		for (const line of wrap(value, usable, 9)) {
			need(13);
			text(line, pad, y + 9, 9, false, color);
			y += 13;
		}
		y += 7;
	};
	const section = (title) => {
		need(65);
		doc.setFillColor(...blue);
		doc.roundedRect(pad, y, usable, 24, 5, 5, "F");
		text(title, pad + 10, y + 16, 11, true, [255, 255, 255]);
		y += 34;
	};
	// Ogni riga va a capo e continua sulla pagina successiva con le intestazioni ripetute.
	const table = (headers, rows, ratios) => {
		const widths = ratios.map((r) => usable * r);
		const header = () => {
			doc.setFillColor(...navy);
			doc.rect(pad, y, usable, 24, "F");
			let x = pad;
			headers.forEach((label, i) => {
				text(label, x + 7, y + 15, 8, true, [255, 255, 255]);
				x += widths[i];
			});
			y += 24;
		};
		need(48);
		header();
		rows.forEach((row, index) => {
			const all = row.map((value, i) => wrap(value, widths[i] - 14, 8.5));
			let offset = 0;
			const count = Math.max(...all.map((lines) => lines.length));
			while (offset < count) {
				if (y + 27 > bottom) {
					doc.addPage();
					y = pad;
					header();
				}
				const lineCount = Math.min(
					count - offset,
					Math.max(1, Math.floor((bottom - y - 12) / 12)),
				);
				const rowHeight = lineCount * 12 + 12;
				doc.setFillColor(...(index % 2 ? [244, 247, 251] : [255, 255, 255]));
				doc.rect(pad, y, usable, rowHeight, "F");
				let x = pad;
				for (let i = 0; i < all.length; i++) {
					const lines = all[i];
					const part = lines.slice(offset, offset + lineCount);
					if (part.length) text(part, x + 7, y + 14, 8.5);
					x += widths[i];
				}
				doc.setDrawColor(...border);
				doc.line(pad, y + rowHeight, pad + usable, y + rowHeight);
				y += rowHeight;
				offset += lineCount;
			}
		});
		y += 14;
	};
	const info = (rows) => table(["Dato", "Valore"], rows, [0.29, 0.71]);
	const sideLabel = (side) => (side === "home" ? "Casa" : "Trasferta");
	const club = (side) =>
		safe(
			side === "home"
				? matchData.homeClub?.name || matchData.homeClub
				: matchData.awayClub?.name || matchData.awayClub,
		);
	const team = (side) =>
		safe(
			side === "home"
				? matchData.homeTeamName || matchData.homeSide?.name
				: matchData.awayTeamName || matchData.awaySide?.name,
			club(side),
		);
	const sideOf = (row) =>
		matchEventBelongsToSide(row, matchData, "home")
			? "home"
			: matchEventBelongsToSide(row, matchData, "away")
				? "away"
				: null;
	const flags = (side) =>
		["Participation", "Scoring", "Discipline"].map(
			(key) => !!matchData[side + key + "Complete"],
		);
	const completeness = (side) => {
		const values = flags(side);
		return values.every(Boolean)
			? "Dati completi"
			: values.some(Boolean) || matchData[side + "ResultGoalsComplete"]
				? "Dati parziali"
				: "Dati mancanti o non confermati";
	};
	const lineup = matchData.matchPlayers || [];
	const events = [...(matchData.matchEvents || [])].sort(compareMatchEvents);
	const statistics = matchData.playerStatistics || [];
	const name = (row) =>
		row?.playerName ||
		personName(row?.player) ||
		personName(teamPlayers.find((p) => p.id === row?.playerId)) ||
		"Giocatore non indicato";
	const eventName = (event, outgoing = false) => {
		const id = outgoing ? event.substitutePlayerId : event.playerId;
		const snapshot = outgoing ? event.outgoingPlayerName : event.playerName;
		const person = outgoing ? event.substitutePlayer : event.player;
		const participant =
			!outgoing && event.scorerParticipationId
				? lineup.find((p) => p.id === event.scorerParticipationId)
				: id
					? lineup.find((p) => p.playerId === id && sideOf(p) === sideOf(event))
					: null;
		const number = outgoing
			? event.substitutePlayerNumber ?? participant?.playerNumber
			: event.playerNumber ?? participant?.playerNumber;
		return (
			(number != null ? "#" + number + " " : "") +
			(snapshot ||
				personName(person) ||
				(participant ? name(participant) : "Marcatore sconosciuto"))
		);
	};

	doc.setFillColor(...navy);
	doc.rect(0, 0, width, 92, "F");
	text("REPORT PARTITA", pad, 38, 21, true, [255, 255, 255]);
	const competition =
		matchData.competitionName || matchData.competitionSeason?.competition?.name;
	text(
		wrap(
			[
				moment(matchData.startDate).format("DD/MM/YYYY HH:mm"),
				competition,
				matchData.resolvedRound ||
					matchData.competitionSeasonGroupRoundName ||
					matchData.round,
			]
				.filter(Boolean)
				.join("  |  "),
			usable,
			9,
		).slice(0, 2),
		pad,
		60,
		9,
		false,
		[225, 235, 250],
	);
	y = 112;
	const score =
		Number.isInteger(matchData.homeClubGoals) &&
		Number.isInteger(matchData.awayClubGoals)
			? matchData.homeClubGoals + " - " + matchData.awayClubGoals
			: "Risultato non inserito";
	text(score, width / 2, y + 19, 19, true, blue, {align: "center"});
	y += 35;
	if (matchData.result) {
		paragraph(
			"Risultato: " +
				({
					events: "dai goal della cronaca",
					manual: "inserito a mano",
					conflict: "da verificare con la cronaca",
					uncollected: "da inserire",
				}[matchData.result.source] || "da verificare"),
		);
		if (matchData.result.warning) paragraph(matchData.result.warning);
		if (matchData.result.conflict)
			paragraph(
				"Manuale: " +
					safe(matchData.result.manualHome) +
					" - " +
					safe(matchData.result.manualAway) +
					"; eventi: " +
					safe(matchData.result.eventHome) +
					" - " +
					safe(matchData.result.eventAway),
			);
	}

	info([
		["Casa", club("home") + " / " + team("home")],
		["Trasferta", club("away") + " / " + team("away")],
	]);
	section("Informazioni e durata sportiva");
	info([
		["Competizione", safe(competition, "Non associata")],
		[
			"Turno",
			safe(
				matchData.resolvedRound ||
					matchData.competitionSeasonGroupRoundName ||
					matchData.round,
			),
		],
		["Tipo partita", safe(matchData.matchType)],
		["Categoria", safe(matchData.playersCategory, "Non indicata")],
		["Campo", safe(matchData.fieldName)],
		[
			"Intervallo calendario",
			moment(matchData.startDate).format("DD/MM/YYYY HH:mm") +
				" - " +
				(matchData.endDate
					? moment(matchData.endDate).format("DD/MM/YYYY HH:mm")
					: "Non indicato"),
		],
		[
			"Durata sportiva",
			matchData.sportingMinutes != null
				? matchData.sportingMinutes + " minuti"
				: "Da definire",
		],
		[
			"Tempi",
			(matchData.sportingPeriods || [])
				.map(
					(p) =>
						p.number +
						"T: " +
						p.durationMinutes +
						" min" +
						(p.isExtraTime ? " (supplementare)" : ""),
				)
				.join("; ") || "Da definire",
		],
		[
			"Rientri nelle sostituzioni",
			matchData.allowReentry ? "Consentiti" : "Non consentiti",
		],
		["Descrizione", safe(matchData.description)],
		...(matchData.notes ? [["Note", matchData.notes]] : []),
		...(matchData.urlVideo ? [["Video", matchData.urlVideo]] : []),
	]);
	paragraph(
		"Il minuto degli eventi e riferito al tempo selezionato. Il recupero e riportato separatamente e non aumenta i minuti sportivi; i supplementari sono inclusi.",
	);
	section("Completezza dei dati per squadra");
	table(
		["Squadra", "Stato", "Conferme"],
		["home", "away"].map((side) => [
			sideLabel(side) + ": " + team(side),
			completeness(side),
			flags(side)
				.map(
					(complete, i) =>
						["Presenze", "Goal/assist", "Cartellini"][i] +
						": " +
						(complete ? "completi" : "non completi"),
				)
				.join("\n") +
				"\nGoal per risultato: " +
				(matchData[side + "ResultGoalsComplete"] ||
				matchData[side + "ScoringComplete"]
					? "confermati"
					: "non confermati"),
		]),
		[0.32, 0.27, 0.41],
	);
	paragraph(
		"Zero significa dato noto; '-' significa dato non raccolto. Nei riepiloghi della distinta, '+' indica un conteggio degli eventi registrati con gruppo non completo.",
	);

	for (const side of ["home", "away"]) {
		section("Distinta - " + sideLabel(side) + ": " + team(side));
		const rows = lineup
			.filter((p) => sideOf(p) === side)
			.sort(
				(a, b) =>
					Number(b.startingPlayer) - Number(a.startingPlayer) ||
					(a.playerNumber ?? 999) - (b.playerNumber ?? 999),
			);
		if (!rows.length) {
			paragraph(
				"Distinta non inserita o non disponibile per le rose abilitate.",
			);
			continue;
		}
		const records = (p, key) => {
			const own = events.filter((e) => sideOf(e) === side);
			const complete = [
				"goal",
				"assist",
				"own_goal",
				"penalty_scored",
				"penalty_missed",
			].includes(key)
				? flags(side)[1]
				: flags(side)[2];
			const count =
				key === "assist"
					? own.filter(
							(e) =>
								e.assistParticipationId === p.id ||
								(p.playerId != null && e.assistPlayerId === p.playerId),
						).length
					: own.filter(
							(e) =>
								(e.scorerParticipationId === p.id ||
									(p.playerId != null && e.playerId === p.playerId)) &&
								(key === "goal"
									? ["goal", "penalty_scored"].includes(e.eventType)
									: e.eventType === key),
						).length;
			return count ? count + (complete ? "" : "+") : complete ? "0" : "-";
		};
		table(
			["N.", "Giocatore / ruolo", "Presenza", "Min.", "Eventi registrati"],
			rows.map((p) => [
				safe(p.playerNumber),
				name(p) + "\n" + safe(p.roleSnapshot || p.player?.primaryRole),
				(p.provisional
					? "Presenza da verificare"
					: p.startingPlayer
						? "Titolare"
						: "Riserva") +
					(p.provisional
						? ""
						: p.hasPlayed === true
							? " / Entrato"
							: p.hasPlayed === false
								? " / Non entrato"
								: " / Da verificare") +
					(p.dismissed
						? "\nEspulso " +
							safe(p.dismissalTime) +
							(p.hasPlayed === false ? " dalla panchina" : "")
						: ""),
				safe(p.calculatedMinutes),
				"Goal " +
					records(p, "goal") +
					"; assist " +
					records(p, "assist") +
					"\nGialli " +
					records(p, "yellow_card") +
					"; doppi gialli " +
					records(p, "double_yellow_card") +
					"; rossi " +
					records(p, "red_card") +
					["own_goal", "penalty_scored", "penalty_missed"]
						.filter((type) =>
							events.some(
								(e) =>
									sideOf(e) === side &&
									e.eventType === type &&
									(e.scorerParticipationId === p.id ||
										(p.playerId != null && e.playerId === p.playerId)),
							),
						)
						.map((type) => "\n" + EVENT_LABELS[type] + ": " + records(p, type))
						.join(""),
			]),
			[0.06, 0.3, 0.26, 0.07, 0.31],
		);
	}
	const convocations = matchData.matchConvocations || [];
	if (convocations.length) {
		section("Convocazioni e disponibilita");
		table(
			["Squadra", "Giocatore", "Stato"],
			convocations.map((c) => {
				const p = lineup.find(
					(p) => p.playerId === c.playerId && sideOf(p) === sideOf(c),
				);
				return [
					sideOf(c)
						? sideLabel(sideOf(c)) + ": " + team(sideOf(c))
						: "Da assegnare",
					c.playerName ||
						(p
							? name(p)
							: personName(c.player) ||
								personName(teamPlayers.find((p) => p.id === c.playerId)) ||
								"Giocatore non indicato"),
					safe(c.status),
				];
			}),
			[0.32, 0.45, 0.23],
		);
	}

	section("Cronologia eventi");
	if (!events.length) paragraph("Nessun evento registrato.");
	else
		table(
			["Tempo / ordine", "Squadra / evento", "Dettaglio"],
			events.map((e) => {
				const side = sideOf(e);
				let detail =
					e.eventType === "substitution"
						? "Esce " + eventName(e, true) + "\nEntra " + eventName(e)
						: eventName(e);
				if (e.assistPlayerId || e.assistParticipationId)
					detail +=
						"\nAssist: " +
						(e.assistPlayerNumber != null
							? "#" + e.assistPlayerNumber + " "
							: "") +
						(e.assistPlayerName ||
							personName(e.assistPlayer) ||
							name(
								lineup.find(
									(p) =>
										sideOf(p) === side &&
										(e.assistParticipationId
											? p.id === e.assistParticipationId
											: e.assistPlayerId && p.playerId === e.assistPlayerId),
								),
							));
				if (["red_card", "double_yellow_card"].includes(e.eventType)) {
					const p = lineup.find(
						(p) => p.playerId === e.playerId && sideOf(p) === side,
					);
					if (p?.hasPlayed === false)
						detail += "\nEspulsione dalla panchina: nessun minuto giocato.";
				}
				if (e.notes) detail += "\nNote: " + e.notes;
				if (e.validationWarning)
					detail += "\nDa verificare: " + e.validationWarning;
				return [
					matchEventTime(e) + "\nOrdine: " + safe(e.sequence, "Precedente"),
					(side
						? sideLabel(side) + ": " + team(side)
						: "Squadra da assegnare") +
						"\n" +
						(e.eventType === "substitution" && e.substitutionReason === "injury"
							? "Sostituzione per infortunio"
							: EVENT_LABELS[e.eventType] || e.eventType),
					detail,
				];
			}),
			[0.24, 0.28, 0.48],
		);

	section("Statistiche dei giocatori del tuo club");
	paragraph(
		"Stessi valori della scheda Statistiche giocatori, per le rose abilitate. I conteggi non vengono sommati ai valori manuali. Da confrontare indica una divergenza ancora da risolvere; il valore manuale e conservato.",
	);
	if (!statistics.length)
		paragraph("Nessuna partecipazione del tuo club disponibile.");
	for (const row of statistics) {
		need(90);
		text(
			"#" +
				safe(row.playerNumber) +
				" " +
				row.playerName +
				" - " +
				sideLabel(row.matchSide),
			pad,
			y + 12,
			10,
			true,
			navy,
		);
		y += 24;
		if (row.warning) paragraph("Attenzione: " + row.warning, [175, 80, 0]);
		table(
			["Statistica", "Valore", "Fonte / confronto"],
			Object.entries(row.metrics).map(([key, metric]) => [
				STAT_LABELS[key] || key,
				safe(metric.value),
				SOURCES[metric.source] +
					(metric.manualValue != null &&
					metric.eventValue != null &&
					metric.manualValue !== metric.eventValue
						? "\nManuale: " +
							metric.manualValue +
							"; eventi: " +
							metric.eventValue
						: ""),
			]),
			[0.35, 0.13, 0.52],
		);
		const details = Object.entries(row.values).filter(
			([key, value]) =>
				value != null &&
				value !== "" &&
				!Object.hasOwn(row.metrics, key) &&
				key !== "goalsConceded",
		);
		if (details.length)
			info(details.map(([key, value]) => [STAT_LABELS[key] || key, value]));
	}
	const notes = matchData.matchNotes || [];
	if (notes.length) {
		section("Note della partita");
		notes.forEach((note) =>
			paragraph(
				note.notes ||
					note.note ||
					note.description ||
					note.text ||
					"Nota senza testo",
			),
		);
	}
	const total = doc.getNumberOfPages();
	for (let page = 1; page <= total; page++) {
		doc.setPage(page);
		doc.setDrawColor(...border);
		doc.line(pad, height - 34, width - pad, height - 34);
		text(
			"Generato il " + moment().format("DD/MM/YYYY HH:mm"),
			pad,
			height - 19,
			8,
			false,
			muted,
		);
		text(
			"Pagina " + page + " / " + total,
			width - pad,
			height - 19,
			8,
			false,
			muted,
			{align: "right"},
		);
	}
	const filename =
		"Partita_" +
		moment(matchData.startDate).format("DD-MM-YYYY") +
		"_" +
		club("home") +
		"_vs_" +
		club("away") +
		".pdf";
	doc.save(filename.replace(/[\/:*?\x22<>|]/g, "-"));
	return doc;
};
